import { emptyProjectOrchestrator as api } from './api';
export const addTagTypes = [
  'ExternalRecruiting',
  'FieldingGroups',
  'FilteredProfile',
  'RecruitAssignments',
  'RecruitProgress',
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      postExternalRecruiting: build.mutation<
        PostExternalRecruitingApiResponse,
        PostExternalRecruitingApiArg
      >({
        query: (queryArg) => ({
          url: `/ExternalRecruiting`,
          method: 'POST',
          body: queryArg.createExternalRecruitingCodeRequest,
        }),
        invalidatesTags: ['ExternalRecruiting'],
      }),
      postFieldingGroupsProjectsByProjectIdFieldingGroupsLaunchSurveysAndBoxes: build.mutation<
        PostFieldingGroupsProjectsByProjectIdFieldingGroupsLaunchSurveysAndBoxesApiResponse,
        PostFieldingGroupsProjectsByProjectIdFieldingGroupsLaunchSurveysAndBoxesApiArg
      >({
        query: (queryArg) => ({
          url: `/FieldingGroups/projects/${queryArg.projectId}/fieldingGroups/$LaunchSurveysAndBoxes`,
          method: 'POST',
        }),
        invalidatesTags: ['FieldingGroups'],
      }),
      postFilteredProfileCsv: build.mutation<
        PostFilteredProfileCsvApiResponse,
        PostFilteredProfileCsvApiArg
      >({
        query: (queryArg) => ({
          url: `/FilteredProfile/$csv`,
          method: 'POST',
          body: queryArg.filterProfileRequest,
        }),
        invalidatesTags: ['FilteredProfile'],
      }),
      postFilteredProfileExportByIdentifier: build.mutation<
        PostFilteredProfileExportByIdentifierApiResponse,
        PostFilteredProfileExportByIdentifierApiArg
      >({
        query: (queryArg) => ({
          url: `/FilteredProfile/$exportByIdentifier`,
          method: 'POST',
          body: queryArg.exportHighlighterProfilesByIdentifierRequest,
        }),
        invalidatesTags: ['FilteredProfile'],
      }),
      getProjectsByProjectIdRecruitassignmentsLatest: build.query<
        GetProjectsByProjectIdRecruitassignmentsLatestApiResponse,
        GetProjectsByProjectIdRecruitassignmentsLatestApiArg
      >({
        query: (queryArg) => ({ url: `/projects/${queryArg.projectId}/recruitassignments/latest` }),
        providesTags: ['RecruitAssignments'],
      }),
      getProjectsByProjectIdRecruitassignmentsAssignmentwithenhancedprofile: build.query<
        GetProjectsByProjectIdRecruitassignmentsAssignmentwithenhancedprofileApiResponse,
        GetProjectsByProjectIdRecruitassignmentsAssignmentwithenhancedprofileApiArg
      >({
        query: (queryArg) => ({
          url: `/projects/${queryArg.projectId}/recruitassignments/$assignmentwithenhancedprofile`,
        }),
        providesTags: ['RecruitAssignments'],
      }),
      postProjectsByProjectIdRecruitassignmentsFinalize: build.mutation<
        PostProjectsByProjectIdRecruitassignmentsFinalizeApiResponse,
        PostProjectsByProjectIdRecruitassignmentsFinalizeApiArg
      >({
        query: (queryArg) => ({
          url: `/projects/${queryArg.projectId}/recruitassignments/$finalize`,
          method: 'POST',
        }),
        invalidatesTags: ['RecruitAssignments'],
      }),
      getProjectsByProjectIdRecruitprogress: build.query<
        GetProjectsByProjectIdRecruitprogressApiResponse,
        GetProjectsByProjectIdRecruitprogressApiArg
      >({
        query: (queryArg) => ({ url: `/projects/${queryArg.projectId}/recruitprogress` }),
        providesTags: ['RecruitProgress'],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as projectOrchestratorApi };
export type PostExternalRecruitingApiResponse =
  /** status 200 OK */ CreateExternalRecruitingCodeResponse;
export type PostExternalRecruitingApiArg = {
  createExternalRecruitingCodeRequest: CreateExternalRecruitingCodeRequest;
};
export type PostFieldingGroupsProjectsByProjectIdFieldingGroupsLaunchSurveysAndBoxesApiResponse =
  /** status 200 OK */ LaunchSurveysAndBoxesResponse;
export type PostFieldingGroupsProjectsByProjectIdFieldingGroupsLaunchSurveysAndBoxesApiArg = {
  projectId: number;
};
export type PostFilteredProfileCsvApiResponse = unknown;
export type PostFilteredProfileCsvApiArg = {
  filterProfileRequest: FilterProfileRequest;
};
export type PostFilteredProfileExportByIdentifierApiResponse =
  /** status 200 OK */ HighlighterProfileExportResult;
export type PostFilteredProfileExportByIdentifierApiArg = {
  exportHighlighterProfilesByIdentifierRequest: ExportHighlighterProfilesByIdentifierRequest;
};
export type GetProjectsByProjectIdRecruitassignmentsLatestApiResponse =
  /** status 200 OK */ RecruitAssignment;
export type GetProjectsByProjectIdRecruitassignmentsLatestApiArg = {
  projectId: number;
};
export type GetProjectsByProjectIdRecruitassignmentsAssignmentwithenhancedprofileApiResponse =
  /** status 200 OK */ AssignmentWithEnhancedProfile[];
export type GetProjectsByProjectIdRecruitassignmentsAssignmentwithenhancedprofileApiArg = {
  projectId: number;
};
export type PostProjectsByProjectIdRecruitassignmentsFinalizeApiResponse =
  /** status 200 OK */ FinalizedAssignment;
export type PostProjectsByProjectIdRecruitassignmentsFinalizeApiArg = {
  projectId: number;
};
export type GetProjectsByProjectIdRecruitprogressApiResponse =
  /** status 200 OK */ RecruitProgressDto;
export type GetProjectsByProjectIdRecruitprogressApiArg = {
  projectId: number;
};
export type CreateExternalRecruitingCodeResponse = {
  id?: string;
};
export type CreateExternalRecruitingCodeRequest = {
  questionIds?: string[];
  optInId?: string;
  codeName: string;
  isRecruitable?: boolean;
};
export type SurveyInstanceResponse = {
  highlighterId?: string;
  status?: string;
  detail?: string | null;
};
export type CreateSurveyInstanceResult = {
  emailsSent?: boolean;
  surveyInstances: SurveyInstanceResponse[];
};
export type Client = {
  clientPortalId?: number | null;
  name?: string | null;
  highlighterFacingName?: string | null;
};
export type Box = {
  id?: string;
  highlighterId?: string;
  kitId?: number | null;
  insertAssetId?: string | null;
  project?: Client;
};
export type LaunchSurveysAndBoxesResponse = {
  surveyResult: CreateSurveyInstanceResult;
  boxesResult: Box[];
};
export type Answer = {
  values?: string[];
  ageMin?: number | null;
  ageMax?: number | null;
};
export type QuotaPartition = {
  answers: Answer;
  min?: number | null;
  max?: number | null;
};
export type Quota = {
  id?: string | null;
  type: string;
  key: string;
  description?: string | null;
  partitions?: QuotaPartition[];
  hasCustomQuestions?: boolean;
};
export type StringStringKeyValuePair = {
  key?: string;
  value?: string;
};
export type TestGroupTask = {
  type: string;
  target: number;
};
export type TestGroupProduct = {
  productId: number;
  order?: number | null;
  countPerTester?: number;
  name?: string | null;
  recipientName?: string | null;
};
export type TestGroup = {
  id?: string | null;
  name?: string | null;
  target?: number;
  placements?: number;
  status?: string;
  quotas?: Quota[];
  customData?: StringStringKeyValuePair[];
  tasks?: TestGroupTask[];
  products?: TestGroupProduct[];
  surveys?: string[];
  hasCriteria?: boolean;
  recruitSource?: string | null;
};
export type InternalRecruitingOptions = {
  excludeProjectIds?: number[];
  includeHighlightEmployees?: boolean;
  sendOptIns?: boolean;
  sendToHighlightEmployeesOnly?: boolean;
  staticAssignment?: boolean;
  tagWeights?: {
    [key: string]: number | null;
  } | null;
  willUseBackendTransform?: boolean;
  restrictRecruitReferralCodeIds?: string[];
};
export type RecruitingFilter = {
  excludeActiveItemSurvey?: boolean | null;
  excludeActiveOptInSurvey?: boolean | null;
  excludeActiveOneOffSurvey?: boolean | null;
  excludePendingItemSurvey?: boolean | null;
  excludeItemSurveyDeadlineDateTimeRangeStart?: string | null;
  excludeItemSurveyDeadlineDateTimeRangeEnd?: string | null;
  boxesMin?: number | null;
  boxesMax?: number | null;
  referralCode?: string | null;
  recruitableBypass?: boolean | null;
  statuses?: string[] | null;
  cities?: string[] | null;
  zipCodes?: string[] | null;
  includeTags?: string[] | null;
  excludeTags?: string[] | null;
};
export type FilterProfileRequest = {
  testGroup?: TestGroup;
  internalOptions?: InternalRecruitingOptions;
  communitySlug?: string;
  filter?: RecruitingFilter;
  questionsThatMustNotBeAnswered?: string[] | null;
};
export type HighlighterProfileExportItem = {
  success: boolean;
  message?: string | null;
  input: string;
  outcome: string;
};
export type HighlighterProfileExportResult = {
  success: boolean;
  message: string;
  items: HighlighterProfileExportItem[];
  csv?: string | null;
};
export type ExportHighlighterProfilesByIdentifierRequest = {
  internalOptions?: InternalRecruitingOptions;
  communitySlug?: string;
  filter?: RecruitingFilter;
  identifiers?: string[] | null;
};
export type HighlighterAssignment = {
  highlighterId?: string;
  candidateTestGroups?: string[];
  assignedTestGroup?: string;
  rotationGroup?: string;
  activityGroup?: string;
  products?: string[];
  testGroupProducts?: TestGroupProduct[];
  customTags?: string[];
  profileData?: {
    [key: string]: any;
  };
  email?: string | null;
};
export type Assignment = {
  highlighterAssignments?: HighlighterAssignment[];
};
export type RecruitAssignment = {
  recruitAssignmentId?: string;
  projectId?: number;
  createdAt?: string;
  updatedAt?: string | null;
  assignment?: Assignment;
};
export type File = {
  filename?: string | null;
  url?: string | null;
  downloadUrl?: string | null;
};
export type AnswerTextWithQualityStats = {
  answer?: string;
  prettyAnswer?: string;
  qualityRating?: string | null;
  qualityJustification?: string | null;
  answerFile?: File;
  flagged?: boolean | null;
  excluded?: boolean | null;
  isMultiSelect?: boolean | null;
};
export type SurveyAnswer = {
  questionId?: string;
  questionLabel?: string;
  shortQuestionLabel?: string | null;
  questionType?: string;
  answers?: string[];
  answerDisplay?: string;
  answerFile?: string;
  answersWithQualityStats?: AnswerTextWithQualityStats[];
};
export type AssignmentWithEnhancedProfile = {
  highlighterId?: string;
  email?: string | null;
  candidateTestGroups?: string[];
  assignedTestGroup?: string;
  rotationGroup?: string;
  products?: string[];
  testGroupProducts?: TestGroupProduct[];
  customTags?: string[];
  surveyResponseStatus?: string;
  profile?: SurveyAnswer[];
};
export type FieldingGroupKit = {
  name: string;
  internalName: string;
  quantity?: number;
  insertId?: number;
  productListId?: number | null;
  insertAssetId?: string | null;
};
export type ProductBase = {
  id?: number;
  recipientName?: string | null;
  name?: string | null;
};
export type ProjectSurveyBase = {
  productIds?: number[];
  projectId?: number;
  products?: ProductBase[];
  surveyId?: number;
  surveyUuid?: string;
  surveyName?: string;
  surveyStatus?: string;
  surveyConfigId?: string | null;
  linkUrl?: string | null;
  surveyRoleId?: string | null;
  ordinal?: number | null;
};
export type FieldingGroupAssignmentBase = {
  id?: string | null;
  highlighterId?: string | null;
  customData?: StringStringKeyValuePair[];
};
export type StepBaseType = 0 | 1 | 2;
export type StepBase = {
  id?: string;
  type: StepBaseType;
  survey?: ProjectSurveyBase;
  surveyId?: number | null;
  duration?: number | null;
  ordinal?: number;
  groupId?: string | null;
  groupedSteps?: StepBase[];
  action?: string | null;
  message?: string | null;
};
export type FieldingGroupBase = {
  id?: string;
  projectId?: number;
  name: string;
  internalName?: string;
  kitId?: number | null;
  kitInfo?: FieldingGroupKit;
  surveys?: ProjectSurveyBase[];
  fieldingGroupAssignments?: FieldingGroupAssignmentBase[];
  studyDuration?: number | null;
  studyIncentive?: number | null;
  steps?: StepBase[];
  status?: string;
  instructionsIntro?: string | null;
  targetN?: number | null;
  customData?: StringStringKeyValuePair[] | null;
  rotationGroupKey?: string | null;
};
export type Highlighter = {
  id?: string;
  email?: string;
  isHighlightEmployee?: boolean;
  tags?: string[];
  optInStatus?: string;
  optInDate?: string;
  referralCode?: string | null;
  boxes?: number | null;
  status?: string | null;
  completedIHUTs?: number | null;
  postalCode?: string | null;
  city?: string | null;
  communitySlug?: string | null;
  phone?: string | null;
  address?: string | null;
  region?: string | null;
  countryCode?: string | null;
  deliveryInstructions?: string | null;
};
export type FinalizedAssignment = {
  fieldingGroups?: FieldingGroupBase[] | null;
  createSurveyInstanceResult?: CreateSurveyInstanceResult;
  highlightersThatCanNotBeAssigned?: Highlighter[] | null;
};
export type PartitionProgress = {
  /** Name of the parition */
  partition?: string;
  /** Min number of panelists required.
    THIS IS NOT A PERCENT */
  min?: number;
  /** Max number of panelists required.
    THIS IS NOT A PERCENT */
  max?: number | null;
  /** How many panelists are currently assigned to this test group. */
  filled?: number;
};
export type StringPartitionProgressArrayKeyValuePair = {
  key?: string;
  value?: PartitionProgress[];
};
export type TestGroupProgress = {
  name?: string;
  /** Placements for the testGroup */
  n?: number;
  /** Targets for the testGroup */
  target?: number;
  /** How many panelists are currently assigned to this test group. */
  filled?: number;
  /** How many panelists are currently assigned to this test group and have completed the testGroup surveys. */
  assignedAndCompletedSurveys?: number;
  /** Key matches the TestGroup.Quota.Key, this is usually the questionId for a profileQuestion
    Value is the ParitionProgress for that Quota. */
  criteria?: StringPartitionProgressArrayKeyValuePair[];
};
export type RecruitProgressDto = {
  testGroups?: TestGroupProgress[];
};
export const {
  usePostExternalRecruitingMutation,
  usePostFieldingGroupsProjectsByProjectIdFieldingGroupsLaunchSurveysAndBoxesMutation,
  usePostFilteredProfileCsvMutation,
  usePostFilteredProfileExportByIdentifierMutation,
  useGetProjectsByProjectIdRecruitassignmentsLatestQuery,
  useGetProjectsByProjectIdRecruitassignmentsAssignmentwithenhancedprofileQuery,
  usePostProjectsByProjectIdRecruitassignmentsFinalizeMutation,
  useGetProjectsByProjectIdRecruitprogressQuery,
} = injectedRtkApi;
